"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { BookOpen, PenLine, Heart, ArrowRight, Mail, Sparkles, FileText, Crown } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const ESSAYS = [
  { title: "Excellence Is an Operating System, Not a Value", excerpt: "The thesis essay. Why values statements fail and systems succeed, and what an operating system for excellence actually contains. The clinical parallel that proves the argument: hospitals do not achieve outcomes by believing in excellence — they systematize it.", readTime: "12 min", category: "Flagship", href: "/insights/excellence-operating-system/", gradient: "from-red/15", accent: "text-red", icon: Crown, iconBg: "bg-gradient-sovereign" },
  { title: "Everything About Leadership, Learned in a Delivery Room", excerpt: "The signature essay on the medical method as leadership. Diagnosis, triage, regulation of the room, protocol and judgment, outcome accountability, and the discipline of doing no harm — all learned at the bedside, all proven across eighteen countries.", readTime: "10 min", category: "Signature", href: "/insights/leadership-delivery-room/", gradient: "from-purple/15", accent: "text-purple", icon: PenLine, iconBg: "bg-gradient-royal" },
  { title: "Why Teams Built Seventy Percent Women", excerpt: "The flagship essay. Not a values statement — an operating decision with a decade of evidence. The talent everyone else overlooks has been the foundation of everything built. The argument from operations, not morality.", readTime: "8 min", category: "Flagship", href: "/insights/seventy-percent-women/", gradient: "from-amber-gold/15", accent: "text-amber-gold", icon: Heart, iconBg: "bg-gradient-premium" },
  { title: "The Operator's Notebook", excerpt: "The biweekly publication. One short, disciplined note on how excellent things run. No filler. No slogans. One principle, made usable, every two weeks. The regular output of an operator who measures by outcomes, not visibility.", readTime: "Biweekly", category: "Series", href: "/the-operators-notebook/", gradient: "from-emerald/15", accent: "text-emerald", icon: Mail, iconBg: "bg-gradient-success" },
  { title: "The 70% Project", excerpt: "The flagship initiative. The operating case — backed by a decade of evidence — for building teams that outperform by hiring the talent the world overlooks.", readTime: "Long-form", category: "Initiative", href: "/the-70-percent-project/", gradient: "from-lime/15", accent: "text-lime", icon: Sparkles, iconBg: "bg-gradient-fresh" },
];

export function SelectedWritingSection() {
  return (
    <Section id="selected-writing" ariaLabel="Selected Writing" background="default">
      <SectionHeader
        icon={BookOpen}
        overline="Selected Writing"
        overlineClassName="text-purple"
        pillClassName="glass-tinted-purple border-purple/20"
        title={
          <>
            Thought Leadership from <span className="text-gradient-royal">The Operator.</span>
          </>
        }
        subtitle="One clinical-grade insight per installment, on the systems, protocols, and disciplines that make excellent things actually run."
      />

      <div className="space-y-4">
        {ESSAYS.map((e, i) => (
          <motion.div
            key={e.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
          >
            <Link href={e.href} className="block group">
              <div className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${e.gradient} to-transparent border border-border/30 hover:scale-[1.01] transition-all`}>
                <div className="grid md:grid-cols-[auto_1fr_auto] gap-5 items-center">
                  <div className={`size-14 rounded-xl ${e.iconBg} flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    <e.icon className="size-7 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`text-caption font-semibold ${e.accent} uppercase tracking-wider`}>{e.category}</span>
                      <span className="text-muted-foreground opacity-30">·</span>
                      <span className="text-caption text-muted-foreground">{e.readTime}</span>
                    </div>
                    <h3 className="text-heading-md mb-2">{e.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{e.excerpt}</p>
                  </div>
                  <ArrowRight className={`size-5 ${e.accent} opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all shrink-0`} />
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}
